import React, { useContext, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Button,
    Table,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    UncontrolledTooltip,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { ButtonLoader } from '../../../components/Loader.js';
import Pagination from '../../../components/Pagination.js';
import { SelectedFacilitiesContext } from '../../../contexts/SelectedFacilitiesContext.js';
import { UserContext } from '../../../contexts/UserContext.js';
import useSavedDestructions from '../../../services/NarcoticDestruction/useSavedDestructions.js';
import useDeleteDestruction from '../../../services/NarcoticDestruction/useDeleteDestruction.js';
import useExportFile from '../../../services/commonServices/useExportFile.js';
import usePrintPDF from '../../../services/commonServices/usePrintPDF.js';
import { hasRolePermission } from '../../../utility-services/rolePermissions.js';

const MAX_LINE_ITEMS_PER_DOCUMENT = 22;

function pick(obj, keys, fallback = '-') {
    if (obj == null || typeof obj !== 'object') return fallback;
    for (const k of keys) {
        if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
        const v = obj[k];
        if (v !== undefined && v !== null && v !== '') return v;
        if (v === 0) return 0;
    }
    return fallback;
}

const getParentId = (item) => item?.narcoticDestructionId ?? item?.NarcoticDestructionId ?? item?.id ?? null;

const formatDate = (v) =>
    v
        ? new Date(v).toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
          })
        : '-';

function SavedDestructions() {
    const history = useHistory();
    const { selectedFacilities, facilities } = useContext(SelectedFacilitiesContext);
    const { user, roles } = useContext(UserContext);
    const canDeleteNarcoticDestruction = hasRolePermission(roles, 'canDeleteNarcotic');
    const canEditNarcoticDestruction = hasRolePermission(roles, 'canEditNarcotic');

    const facilityIds = useMemo(() => {
        return (selectedFacilities || []).map((f) => f?.facID).filter((id) => id != null && id !== '');
    }, [selectedFacilities]);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(10);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activeExport, setActiveExport] = useState(null);
    const detailRef = React.useRef(null);

    const { savedDestructionsRaw, isSavedDestructionsLoading, refetchSavedDestructions } = useSavedDestructions(
        facilityIds,
        pageNumber,
        pageSize
    );
    const { exportFile, isExportFileLoading } = useExportFile();
    const { printPDF, isPrintPDFLoading } = usePrintPDF();
    const { deleteDestruction, isDeleteDestructionLoading } = useDeleteDestruction();

    const rows = savedDestructionsRaw?.items ?? [];
    const totalCount = Number(savedDestructionsRaw?.totalCount ?? rows.length ?? 0);
    const totalPages = Number(savedDestructionsRaw?.totalPages ?? Math.max(1, Math.ceil(totalCount / (pageSize || 10))));

    // Fetch all saved destructions (no pagination) so 'Select Only Mine' can select matches across every page
    const [myDestructionsMode, setMyDestructionsMode] = useState(false);
    const allRowsPageSize = Math.max(totalCount || pageSize, pageSize);
    const { savedDestructionsRaw: allSavedDestructionsRaw, isSavedDestructionsLoading: allSavedDestructionsLoading } =
        useSavedDestructions(facilityIds, 1, allRowsPageSize);
    const allRows = allSavedDestructionsRaw?.items ?? [];

    const normalizeValue = (value) => (value === undefined || value === null ? '' : String(value).trim().toLowerCase());
    const currentUserIds = useMemo(
        () => [user?.userId, user?.id, user?.UserId].map(normalizeValue).filter(Boolean),
        [user]
    );
    const isCurrentUserRecord = (item) => {
        const ids = [item?.userId, item?.UserId].map(normalizeValue).filter(Boolean);
        return ids.some((v) => currentUserIds.includes(v));
    };
    const allMyDestructions = useMemo(
        () => allRows.filter(isCurrentUserRecord),
        [allRows, currentUserIds]
    );
    const isMyDestructionsSelected =
        allMyDestructions.length > 0 &&
        allMyDestructions.every((row) => selectedRows.some((r) => getParentId(r) === getParentId(row)));

    const handleSelectOnlyMyDestructions = () => {
        if (myDestructionsMode) {
            setSelectedRows([]);
            setMyDestructionsMode(false);
        } else {
            setSelectedRows(allMyDestructions);
            setMyDestructionsMode(true);
        }
    };

    React.useEffect(() => {
        if (!myDestructionsMode) return;
        setSelectedRows(allMyDestructions);
    }, [allMyDestructions, myDestructionsMode]);

    React.useEffect(() => {
        if (selectedRecord && detailRef.current) {
            detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedRecord]);

    const selectedItems = selectedRecord?.items ?? [];

    const hasSelectedRows = selectedRows?.length > 0;
    const exportDisabledTooltip = 'Select one or more table rows to enable export options.';
    const facilityOptions = facilities.filter((fac) => fac?.facName != null && fac?.facID != null);

    const toggleRowSelection = (item) => {
        const id = getParentId(item);
        setSelectedRows((prev) =>
            prev.some((r) => getParentId(r) === id) ? prev.filter((r) => getParentId(r) !== id) : [...prev, item]
        );
    };

    const toggleDropdown = () => {
        if (!hasSelectedRows) return;
        setDropdownOpen(!dropdownOpen);
    };

    const hasMultipleSelectedFacilities = () => {
        const uniqueFacilityIds = new Set(
            (selectedRows || []).map((item) => pick(item, ['facId'], '')).filter((facId) => facId !== '')
        );
        return uniqueFacilityIds.size > 1;
    };

    const showSingleFacilityValidation = () => {
        Swal.fire({
            icon: 'info',
            text: 'Please select records from 1 facility before proceeding.',
            customClass: {
                confirmButton: 'custom-green-button',
            },
        });
    };

    const buildExportParams = (selectedIds) => {
        const params = new URLSearchParams();
        selectedIds.forEach((id) => params.append('ids', id));
        params.append('exportType', 'Excel');
        return params;
    };

    const handleExport = (type, url, fileName) => {
        if (!hasSelectedRows) {
            Swal.fire({
                icon: 'info',
                text: exportDisabledTooltip,
                customClass: { confirmButton: 'custom-green-button' },
            });
            return;
        }

        if (facilityOptions.length > 1 && hasMultipleSelectedFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        const selectedIds = (selectedRows || [])
            .flatMap((parent) => (Array.isArray(parent?.items) ? parent.items : []))
            .map((item) => item?.id)
            .filter((id) => id !== undefined && id !== null && id !== '');

        setActiveExport(type);
        exportFile(
            { url, fileName, params: buildExportParams(selectedIds) },
            { onSettled: () => setActiveExport(null) }
        );
    };

    const handleDeleteDestruction = (parent) => {
        const items = Array.isArray(parent?.items) ? parent.items : [];
        const itemIds = items.map((item) => item?.id).filter((id) => id !== undefined && id !== null);
        if (itemIds.length === 0) return;

        const parentId = getParentId(parent);
        Swal.fire({
            title: 'Delete saved destruction?',
            text: `This will permanently delete destruction #${parentId} and all ${itemIds.length} of its line items.`,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Delete',
            customClass: { confirmButton: 'custom-green-button', cancelButton: 'custom-cancel-button' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteDestruction(itemIds, {
                onSuccess: () => {
                    setSelectedRecord(null);
                    setSelectedRows((prev) => prev.filter((r) => getParentId(r) !== parentId));
                    refetchSavedDestructions();
                },
            });
        });
    };

    const handleEditDestruction = (parent) => {
        const items = Array.isArray(parent?.items) ? parent.items : [];
        if (items.length === 0) return;
        history.push({
            pathname: '/utilities/narcotic-destruction',
            state: {
                editItems: items,
                facId: pick(parent, ['facId'], ''),
                narcoticDestructionId: getParentId(parent),
            },
        });
    };

    const handlePrint = () => {
        if (!hasSelectedRows) {
            Swal.fire({
                icon: 'info',
                text: exportDisabledTooltip,
                customClass: { confirmButton: 'custom-green-button' },
            });
            return;
        }

        if (facilityOptions.length > 1 && hasMultipleSelectedFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        const selectedItems = (selectedRows || []).flatMap((parent) =>
            Array.isArray(parent?.items) ? parent.items : []
        );
        const lineItems = selectedItems.slice(0, MAX_LINE_ITEMS_PER_DOCUMENT).map((item) => ({
            rxNo: pick(item, ['rxNumber']),
            facId: pick(item, ['facId']),
            partialCount: String(pick(item, ['quantity'], '')),
            totalDestroyed: String(pick(item, ['quantity'], '')),
            method: pick(item, ['destructionMethod']),
            reason: pick(item, ['reason']),
            destroyedBy: user?.displayName,
            witnessby: pick(item, ['witnessName']),
        }));

        printPDF(
            {
                url: '/destructions/generate-pdf',
                fileName: 'State_Destruction_PDF.pdf',
                downloadOnSuccess: true,
                payload: {
                    payload: { items: lineItems },
                    ids: selectedItems.map((item) => item?.id),
                },
            },
            {
                onSuccess: () => {
                    refetchSavedDestructions();
                    setSelectedRows([]);
                },
            }
        );
    };

    return (
        <div className="page-content">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="mb-0">Saved Destructions</h2>
                <Button
                    style={{ marginTop: '10px' }}
                    color="secondary"
                    outline
                    tag={Link}
                    to="/utilities/narcotic-destruction">
                    Back to narcotic destruction
                </Button>
            </div>
            <Card className="p-2">
                <Row>
                    <Col md={12}>
                        {isSavedDestructionsLoading ? (
                            <div className="text-center p-5">
                                <ButtonLoader />
                            </div>
                        ) : (
                            <>
                                <Table className="table-bordered" responsive>
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{ width: '40px', textAlign: 'center' }} aria-label="Select" />
                                            <th>Destruction ID</th>
                                            <th>Facility</th>
                                            <th>Form Date</th>
                                            <th>Items Count</th>
                                            <th>Created By</th>
                                            <th>Updated By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length > 0 ? (
                                            rows.map((item, index) => {
                                                const id = getParentId(item);
                                                const isSelected =
                                                    id != null && getParentId(selectedRecord) === id;
                                                const isChecked = selectedRows.some((r) => getParentId(r) === id);
                                                return (
                                                    <tr
                                                        key={id ?? index}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: isSelected ? '#e8f0ff' : undefined,
                                                        }}
                                                        onClick={() => setSelectedRecord(isSelected ? null : item)}>
                                                        <td
                                                            style={{ textAlign: 'center' }}
                                                            onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => toggleRowSelection(item)}
                                                            />
                                                        </td>
                                                        <td>{id ?? '-'}</td>
                                                        <td>{pick(item, ['facId'])}</td>
                                                        <td>{formatDate(pick(item, ['formDate'], null))}</td>
                                                        <td>{Array.isArray(item?.items) ? item.items.length : 0}</td>
                                                        <td>{pick(item, ['createdBy'])}</td>
                                                        <td>{pick(item, ['updatedBy'])}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center p-4">
                                                    No saved destructions found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                <div className="mt-2">
                                    <Pagination
                                        pageNumber={pageNumber}
                                        pageSize={pageSize}
                                        totalCount={totalCount}
                                        totalPages={totalPages}
                                        onPageChange={(p) => setPageNumber(p)}
                                    />
                                </div>
                                <div className="d-flex justify-content-between align-items-center px-2 mt-2">
                                    <div className="d-flex align-items-center">
                                        <Button
                                            color="gray"
                                            style={{
                                                cursor: 'pointer',
                                                border: '1px solid #E5E7EB',
                                                borderRadius: '5px',
                                                padding: '10px 10px',
                                                color: '#6B7280',
                                            }}
                                            className="btn-sm"
                                            onClick={handleSelectOnlyMyDestructions}
                                            disabled={allSavedDestructionsLoading || allMyDestructions.length === 0}>
                                            {isMyDestructionsSelected
                                                ? 'Unselect Only My Destructions'
                                                : 'Select Only My Destructions'}
                                        </Button>
                                        <div style={{ fontSize: '13px' }} className="text-muted ml-2">
                                            {selectedRows?.length || 0} records selected
                                        </div>
                                    </div>
                                    <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                                        <span id="saved-destructions-export-tooltip" className="d-inline-block">
                                            <DropdownToggle
                                                caret
                                                color="primary"
                                                className="btn-sm"
                                                disabled={!hasSelectedRows}
                                                style={!hasSelectedRows ? { pointerEvents: 'none', opacity: 1 } : undefined}>
                                                Export
                                            </DropdownToggle>
                                        </span>
                                        {!hasSelectedRows && (
                                            <UncontrolledTooltip placement="top" target="saved-destructions-export-tooltip">
                                                {exportDisabledTooltip}
                                            </UncontrolledTooltip>
                                        )}
                                        <DropdownMenu right>
                                            <DropdownItem
                                                onClick={handlePrint}
                                                disabled={isPrintPDFLoading || !hasSelectedRows}>
                                                {isPrintPDFLoading ? 'Exporting...' : 'Print State Destruction Form'}
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() =>
                                                    handleExport(
                                                        'excel',
                                                        '/destructions/export',
                                                        'narcotic-destruction.xlsx'
                                                    )
                                                }
                                                disabled={
                                                    (isExportFileLoading && activeExport === 'excel') ||
                                                    rows.length === 0 ||
                                                    !hasSelectedRows
                                                }>
                                                {isExportFileLoading && activeExport === 'excel'
                                                    ? 'Exporting...'
                                                    : 'Export Excel'}
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </Card>
            {selectedRecord && (
                <Card className="p-2 mt-3" innerRef={detailRef}>
                    <div className="mb-3">
                        <h4>Destruction #{getParentId(selectedRecord)}</h4>
                        <div>
                            <strong>Facility:</strong> {pick(selectedRecord, ['facId'])}
                        </div>
                        <div>
                            <strong>Form Date:</strong> {formatDate(pick(selectedRecord, ['formDate'], null))}
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', width: '100%' }}>
                        <Table
                            bordered
                            style={{ minWidth: '1200px', whiteSpace: 'nowrap', marginBottom: 0 }}>
                            <thead className="thead-light">
                                <tr>
                                    <th className="p-3">Rx #</th>
                                    <th className="p-3">Resident</th>
                                    <th className="p-3">Drug Name</th>
                                    <th className="p-3">NDC</th>
                                    <th className="p-3">Dispense Date</th>
                                    <th className="p-3">Quantity</th>
                                    <th className="p-3">Destruction Date</th>
                                    <th className="p-3">Method</th>
                                    <th className="p-3">Reason</th>
                                    <th className="p-3">Nurse</th>
                                    <th className="p-3">Witness</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedItems.length > 0 ? (
                                    selectedItems.map((drug, idx) => (
                                        <tr key={drug?.id ?? idx}>
                                            <td className="p-3">{pick(drug, ['rxNumber'])}</td>
                                            <td className="p-3">{pick(drug, ['residentName'])}</td>
                                            <td className="p-3">{pick(drug, ['drugName'])}</td>
                                            <td className="p-3">{pick(drug, ['ndc'])}</td>
                                            <td className="p-3">{formatDate(pick(drug, ['dispenseDate'], null))}</td>
                                            <td className="p-3">{pick(drug, ['quantity'])}</td>
                                            <td className="p-3">{formatDate(pick(drug, ['destructionDate'], null))}</td>
                                            <td className="p-3">{pick(drug, ['destructionMethod'])}</td>
                                            <td className="p-3">{pick(drug, ['reason'])}</td>
                                            <td className="p-3">{pick(drug, ['nurseName'])}</td>
                                            <td className="p-3">{pick(drug, ['witnessName'])}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="text-center p-4">
                                            No items found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    <div className="d-flex justify-content-end mt-3" style={{ gap: '10px' }}>
                        {canDeleteNarcoticDestruction && (
                            <Button
                                size="sm"
                                color="danger"
                                outline
                                onClick={() => handleDeleteDestruction(selectedRecord)}
                                disabled={isDeleteDestructionLoading}>
                                {isDeleteDestructionLoading ? <ButtonLoader /> : 'Delete'}
                            </Button>
                        )}
                        {canEditNarcoticDestruction && (
                            <Button size="sm" color="primary" onClick={() => handleEditDestruction(selectedRecord)}>
                                Edit
                            </Button>
                        )}
                    </div>
                </Card>
            )}
        </div>
    );
}

export default SavedDestructions;
